import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { toaster } from 'components/v2/toast';
import { Command, GLOBAL_OPTION, ProcessStatus } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import CompanyService from 'services/CompanyService';
import { VerificationType } from 'services/EpdClient';
import { useConnectEpdCertificationBody } from 'services/api/mutations';
import axios, { AxiosError } from 'axios';
import styled from 'styled-components';
import { EPDVerifierCompanyModel, EPDVerifierModel, Option } from 'types/types';

import { useVerifierMarketplaceCountriesLookup } from 'hooks/lookups';
import { useScrollShadow } from 'hooks/useScrollShadow';
import { ScrollShadowDialog } from 'styles/Styles.styled';
import CertificationBodiesList from './CertificationBodiesList';
import { HelpBox, HelpBoxBoldText } from 'components/v2/help-boxes';
import { HyperLink } from 'styles/Styles.styled';
import EpdRoutes from 'routes/EpdRoutes';

type TProps = {
  epdId?: string | undefined;
  epdVersionId?: string | undefined;
  processCertificateIssuedBy: EPDVerifierModel | null;
  onHide: any;
  isOpened: boolean;
};

const CertificationBodySelectorDialog = ({
  epdId,
  epdVersionId,
  processCertificateIssuedBy,
  onHide,
  isOpened,
  ...props
}: TProps) => {
  const { companyId } = useContext(CompanyContext);
  const history = useHistory();
  const [companyBodies, setCompanyBodies] = useState<EPDVerifierCompanyModel[]>([]);
  const [filteredCompanyBodies, setFilteredCompanyBodies] = useState<EPDVerifierCompanyModel[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const [selectedBody, setSelectedBody] = useState<EPDVerifierCompanyModel | undefined>();
  const [inquiryStatus, setInquiryStatus] = useState<[ProcessStatus, Command]>([ProcessStatus.None, Command.None]);

  const { isScrolled, contentRef } = useScrollShadow();

  const countries = useVerifierMarketplaceCountriesLookup().data || [];

  const connectBodyMutation = useConnectEpdCertificationBody(epdVersionId!);

  const onChangeBodyCountry = async (propertyName: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const onAddBody = async (bodyId: string) => {
    try {
      setInquiryStatus([ProcessStatus.Fetching, Command.Connect]);
      await connectBodyMutation.mutateAsync({ epdId, epdVersionId, bodyId });
      setInquiryStatus([ProcessStatus.Success, Command.Connect]);
      toaster({
        severity: 'success',
        summary: 'The body was added to the EPD!',
        details: 'Now the EPD body has an access to the EPD.',
      });
    } catch (error) {
      setInquiryStatus([ProcessStatus.Error, Command.Connect]);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ detail?: string }>;
        if (
          axiosError.response?.status === 403 &&
          axiosError.response.data?.detail === 'You do not have permission to view this EPD.'
        ) {
          toaster({
            severity: 'error',
            summary: 'Access denied',
            details: 'You do not have permission to view this EPD.',
          });
          onHide();
          history.push(EpdLinks.dashboard());
          return;
        }
      }
      setFilteredCompanyBodies((prev) =>
        prev.map((b) => (b.id === selectedBody?.id ? { ...b, errorText: 'Something went wrong.' } : b))
      );
    }
  };

  useEffect(() => {
    const fetchCertificationBodies = async () => {
      let result = await CompanyService.getCompanyCertificationBodies(
        companyId,
        selectedCountry?.value,
        selectedCountry?.label === GLOBAL_OPTION.label ? true : undefined,
        epdVersionId,
        VerificationType.EPDProcessCertification
      );
      setCompanyBodies(result);
      setFilteredCompanyBodies(result);
    };

    fetchCertificationBodies();
  }, [companyId, selectedCountry, isOpened]);

  const handleSearchBodies = (event: any) => {
    const searchTerm = event.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredCompanyBodies(companyBodies);
      return;
    }
    setFilteredCompanyBodies((prev) =>
      companyBodies.filter(
        (item) => item.name?.toLowerCase().includes(searchTerm) || item.email?.toLowerCase().includes(searchTerm)
      )
    );
  };

  return (
    <StyledDialog
      header="Accredited Certification Body"
      $isScrolled={isScrolled}
      visible={isOpened}
      resizable={false}
      style={{ width: '35rem' }}
      contentStyle={!!companyBodies.length ? {} : { overflowY: 'initial' }}
      onHide={() => {
        setSelectedBody(undefined);
        setSelectedCountry(undefined);
        onHide();
      }}
      {...props}
    >
      <DialogContentContainer ref={contentRef}>
        <HelpBox>
          <span>
            Visit the{' '}
            <HyperLink target="_blank" rel="noopener noreferrer" href={EpdRoutes.resources.contactVerifier}>
              Contact verifier
            </HyperLink>{' '}
            page to browse Approved Certification Bodies and send an email with the required collaboration details to the
            selected certification body.
          </span>
          <span>
            After confirmation, use the <HelpBoxBoldText>"Add body"</HelpBoxBoldText> button here to assign the certification
            body to your EPD.
          </span>
        </HelpBox>
        <DialogContentRow>
          <CompilerSelectControlledComponent
            label="Country"
            tooltipPosition={'right'}
            name="bodyCountry"
            options={countries}
            value={selectedCountry}
            onChanged={onChangeBodyCountry}
            placeholder="Select"
            isClearable={true}
          />
        </DialogContentRow>
        <DialogContentRow>
          <WizardTextInputControlledComponent
            label="Certification body name or email"
            tooltipPosition={'right'}
            value={undefined}
            placeholder="Type here to search"
            onChange={handleSearchBodies}
            onChanged={() => {}}
            name="nameOrEmail"
            maxLength={150}
          />
        </DialogContentRow>
        <CertificationBodiesList
          certificationBody={processCertificateIssuedBy}
          bodies={filteredCompanyBodies}
          selectedVerifier={selectedBody}
          onSelectVerifier={setSelectedBody}
          onAddBody={onAddBody}
          buttonsAreDisabled={
            (inquiryStatus[0] === ProcessStatus.Fetching && inquiryStatus[1] === Command.Inquiry) ||
            (inquiryStatus[0] === ProcessStatus.Fetching && inquiryStatus[1] === Command.Connect)
          }
        />
      </DialogContentContainer>
    </StyledDialog>
  );
};

const StyledDialog = styled(ScrollShadowDialog)`
  .p-dialog-header-close svg path {
    fill: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const InformationContainer = styled.div`
  background-color: ${(props) => props.theme.colors.elementsFilledInput};
  text-align: justify;
  padding: 1rem;
  border-radius: 4px;
`;

const DialogContentContainer = styled.div`
  padding: 0.25rem 2rem 2rem 2rem;
`;

const DialogContentRow = styled.div`
  margin-top: 25px;
`;

export default CertificationBodySelectorDialog;
